import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderApiService } from '@digilize/shared/data/services';
import { TranslocoService } from '@ngneat/transloco';
import { AppContext } from '@plys/src/application/app.context';
import { SKIP_ERROR_INTERCEPTOR } from '@plys/src/application/errors/error.interceptor';
import { CartsApiService } from '@plys/src/infrastructure/carts.service';
import {
  Bioscoopcard,
  CartDiscountCode,
  CartDto,
  CartSummaryLineDto,
  CartSummaryOrderDto,
  CartSummaryProductDto,
  /* Aliased: the local `CartStatus` name is the cart-item interface below. */
  CartStatus as CartLifecycle,
  IntegrationReservation,
  RestaurantReservation,
  SubscriptionCardAttachDto,
} from '@plys/src/infrastructure/dto/products.dto';
import { CartSubscriptionCardLineDto } from '@plys/src/infrastructure/dto/subscription.dto';
import { PaulyOrderService } from '@plys/src/infrastructure/order.service';
import { ProductsService } from '@plys/src/infrastructure/products.service';
import { ReservingService } from '@plys/src/infrastructure/reserving.service';
import { SubscriptionShopService } from '@plys/src/infrastructure/subscription-shop.service';
import { PaymentMethodViewData } from '@plys/src/models/payment';
import { ReservationUserData } from '@plys/src/models/reservation';
import { GiftCardCartModel } from '@digilize/shared/definitions/src/lib/models';
import {
  DeliveryType,
  GiftCard,
  Order,
  OrderPayment,
  OrderRequest,
  OrderStatus,
  PaymentMethod,
  RestaurantReservationModel,
} from '@shared/definitions';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { SeatsReservationResponse, SeatsService } from '../../infrastructure/seats.service';
import { CartItem, CartStatus, resolveRowDiscount } from '../../models/cart-item';
import { Product, ProductType, Supplement } from '../../models/product';
import { Seat } from '../../models/seat';
import { cartRequiresAccount } from '../../models/subscription';
import { Ticket } from '../../models/ticket';
import { ClarityService } from '../services/clarity.service';
import { ShopSessionService } from '../services/shop-session.service';
import { SeatsState } from './seats.state';
import { ShowState } from './show.state';

export const MANUALLY_EXCLUSIVE_ERROR = 'errors.has_manually_exclusive_item';
export const BIOSCOOPCARD_EXPIRED_ERROR = 'bioscoopbon.error_expired';
export const BIOSCOOPCARD_NO_BALANCE_ERROR = 'bioscoopbon.error_no_balance';
export const GIFTCARD_NO_BALANCE_ERROR = 'errors.gift_card_balance_insufficient';

@Injectable({
  providedIn: 'root',
})
export class CartState {
  private readonly cartId = new BehaviorSubject<string>(null);
  private readonly tickets = new BehaviorSubject<Ticket[]>([]);
  private readonly giftCards = new BehaviorSubject<GiftCard[]>([]);
  /**
   * Pre-spend balances by gift-card code, learned from the connected-cards listing and from the
   * balance lookup a typed code already goes through. The order path reports applied rows with a
   * null balance, so without this there is no way to tell a card that was fully consumed from one
   * with money left on it (PAUL-3203).
   */
  private readonly giftCardBalances = new Map<string, number>();
  private readonly discounts = new BehaviorSubject<CartDiscountCode[]>([]);
  private readonly boscoopcards = new BehaviorSubject<Bioscoopcard[]>([]);
  private readonly items = new BehaviorSubject<CartItem[]>([]);
  private readonly lines = new BehaviorSubject<CartSummaryLineDto[]>([]);
  private readonly deliveryMethod = new BehaviorSubject<DeliveryType>(DeliveryType.Unknown);
  private readonly total = new BehaviorSubject(0);
  private readonly pendingAmount = new BehaviorSubject(0); /* amount after giftcard */
  /** Backend-computed "Totaal €X per maand" figure; 0 while the cart has no subscription lines. */
  private readonly totalRecurringMonthly = new BehaviorSubject(0);
  readonly totalRecurringMonthly$ = this.totalRecurringMonthly.asObservable();
  /**
   * Whether anything in the cart may only be bought by a logged-in buyer, as the cart summary reports
   * it (see {@link cartRequiresAccount}). Null until a summary has been seen — {@link AccountRequirement}
   * is what turns that into an answer.
   */
  private readonly requiresAccount = new BehaviorSubject<boolean | null>(null);
  readonly requiresAccount$ = this.requiresAccount.asObservable();
  /** Synchronous read for the account step's click handlers, which answer before navigating. */
  get requiresAccountValue(): boolean | null {
    return this.requiresAccount.value;
  }
  private readonly reserving = new BehaviorSubject(false);
  private readonly time = new BehaviorSubject<number | null>(null);
  private readonly timedOut = new BehaviorSubject<boolean>(false);
  private readonly _status = new BehaviorSubject<CartDto['status']>(null);
  status$ = this._status.asObservable();
  get status() {
    return this._status.value;
  }
  set status(status: CartDto['status']) {
    this._status.next(status);
  }
  public currentTicketsInReserveringPhase = 0;
  private _pendingOrderDirty = false;
  /** {@link applyIfFresh} — `server_time` of the newest cart view painted, 0 before the first one. */
  private lastAppliedServerTime = 0;

  private readonly _pendingOrder = new BehaviorSubject<
    (Order & { payment_url: string; payment_methods: PaymentMethod[] }) | null
  >(null);
  pendingOrder$ = this._pendingOrder.asObservable();
  /**
   * The lines of the order the payment step is about to charge — empty until that order exists.
   *
   * The cart's own lines ({@link lines$}) stay the app's line identity: seats, subscription cards and
   * the +/- counters are all keyed on them. This is only about money, and about one field in
   * particular: a subscription's `subscriber.setup_fee_amount` is reported on the order and not on the
   * cart, so the payment summary reads the fee it breaks out from here. The shared `CartLine` type
   * predates that block, hence the cast.
   */
  readonly orderLines$: Observable<CartSummaryLineDto[]> = this._pendingOrder.pipe(
    map((order) => (order?.summary?.lines ?? []) as unknown as CartSummaryLineDto[])
  );
  readonly holdBlockers$ = this._pendingOrder.pipe(map((order) => order?.hold_blockers ?? null));
  readonly discountBlockers$ = this._pendingOrder.pipe(map((order) => order?.hold_blockers?.blockers ?? []));
  deferredPayments$ = combineLatest([this._pendingOrder, this.boscoopcards]).pipe(
    map(([order, boscoopcards]) => {
      const bioscoopIds = new Set(boscoopcards.map((b) => b.id));
      return (
        order?.summary?.payments?.filter(
          (p) => !bioscoopIds.has(p.id) && p.status !== 'processing' && p.status !== 'deferred'
        ) ?? []
      );
    })
  );

  get isOrderCreated(): boolean {
    return !!this._pendingOrder.value && !this._pendingOrderDirty;
  }

  private readonly _cartOrders = new BehaviorSubject<CartSummaryOrderDto[]>([]);

  getCartOrdersValue(): CartSummaryOrderDto[] {
    return this._cartOrders.value;
  }

  private _paymentMethod = new BehaviorSubject<{ methodCost: number | undefined; methodId: string }>(null);
  paymentMethod$ = this._paymentMethod.asObservable();
  get paymentMethod() {
    return this._paymentMethod.value;
  }
  set paymentMethod(paymentMethod: { methodCost: number; methodId: string }) {
    this._paymentMethod.next(paymentMethod);
  }

  private _integrationReservations = new BehaviorSubject<IntegrationReservation[]>(null);
  restaurantReservations$ = this._integrationReservations
    .asObservable()
    .pipe(
      map((reservations) =>
        (reservations ?? []).filter((reservation) => reservation.type === 'etender').map((res) => res.restaurant)
      )
    );

  get integrationReservations() {
    return this._integrationReservations.value;
  }

  private readonly expiredError = new BehaviorSubject(null);
  timerSub: Subscription;
  constructor(
    private context: AppContext,
    private seatsService: SeatsService,
    private orderService: OrderApiService,
    private cartsService: CartsApiService,
    private productsService: ProductsService,
    private cartsApiService: CartsApiService,
    private seatsState: SeatsState,
    private translocoService: TranslocoService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private reservingService: ReservingService,
    private paulyOrderService: PaulyOrderService,
    private clarityService: ClarityService,
    private shopSessionService: ShopSessionService,
    private subscriptionShopService: SubscriptionShopService,
    private showState: ShowState
  ) {
    this.shopSessionService.sessionClaimed$.subscribe(() => this.refreshHoldBlockers());
  }

  get cartId$(): Observable<string> {
    return this.cartId.asObservable();
  }

  get tickets$(): Observable<Ticket[]> {
    return this.tickets.asObservable();
  }

  get cvTickets$(): Observable<Ticket[]> {
    return this.tickets
      .asObservable()
      .pipe(map((tickets) => tickets.filter((ticket) => ticket.product.is_cineville_ticket)));
  }

  get hasCinevilleTickets$(): Observable<boolean> {
    return this.cvTickets$.pipe(map((tickets) => tickets.length > 0));
  }

  get prepaidTicket$(): Observable<Ticket[]> {
    return this.tickets
      .asObservable()
      .pipe(map((tickets) => tickets.filter((ticket) => ticket.product?.is_subscription_ticket)));
  }

  /**
   * A subscription (prepaid) ticket is admitted against a card, so it can never be part of a
   * reservation that is paid for later — the same rule Cineville tickets already carry.
   */
  get hasSubscriptionTickets$(): Observable<boolean> {
    return this.prepaidTicket$.pipe(map((tickets) => tickets.length > 0));
  }

  get lines$(): Observable<CartSummaryLineDto[]> {
    return this.lines.asObservable();
  }

  get deliveryMethod$(): Observable<DeliveryType> {
    return this.deliveryMethod.asObservable();
  }

  get getLinesWithReservation$(): Observable<CartSummaryLineDto[]> {
    return this.lines.pipe(
      map((items) => {
        return items.filter((i) => i.product_has_reservation);
      })
    );
  }

  get items$(): Observable<CartItem[]> {
    return this.items.asObservable();
  }

  get giftCards$(): Observable<GiftCard[]> {
    return this.giftCards.asObservable().pipe(map((cards) => cards.filter((card) => !!card.can_delete)));
  }

  get discounts$(): Observable<CartDiscountCode[]> {
    return this.discounts.asObservable();
  }

  get boscoopcards$(): Observable<Bioscoopcard[]> {
    return this.boscoopcards.pipe(map((cards) => cards.filter((c) => c.applied_amount > 0)));
  }

  get displayUniqueItems$(): Observable<CartItem[]> {
    return this.items$.pipe(
      map((items) => {
        const uniqueItems = new Map<string, CartItem>();
        items.forEach((item) => {
          if (!uniqueItems.has(item.productId)) {
            uniqueItems.set(item.productId, item);
          }
        });
        return Array.from(uniqueItems.values());
      })
    );
  }

  get total$(): Observable<number> {
    return this.total.asObservable();
  }

  get pendingAmount$(): Observable<number> {
    return this.pendingAmount.asObservable();
  }

  get pendingAmountValue(): number {
    return this.pendingAmount.value;
  }

  get boscoopcardsValue(): Bioscoopcard[] {
    return this.boscoopcards.value.filter((c) => c.applied_amount > 0);
  }

  get giftCardsValue(): GiftCard[] {
    return this.giftCards.value;
  }

  get seatsAssigned$(): Observable<boolean> {
    return this.tickets$.pipe(map((tickets) => tickets.some((t) => t.product !== null)));
  }

  get expiredError$(): Observable<boolean> {
    return this.expiredError.asObservable();
  }

  get hasNotAssignedSeats$(): Observable<boolean> {
    return this.tickets$.pipe(map((tickets) => tickets.some((t) => t.seat === null)));
  }

  get reserving$(): Observable<boolean> {
    return this.reserving.asObservable();
  }

  get ticketsWithRequiredRestaurantPlace$(): Observable<Ticket[]> {
    return this.tickets$.pipe(map((tickets) => tickets.filter((ticket) => ticket.product.hasReservation)));
  }

  getReservingValue(): boolean {
    return this.reserving.value;
  }

  get time$(): Observable<number | null> {
    return this.time.asObservable();
  }

  get timedOut$(): Observable<boolean> {
    return this.timedOut.asObservable();
  }

  setTickets(tickets: Ticket[]): void {
    this.tickets.next(tickets);
  }

  setTimedOut(value: boolean) {
    this.timedOut.next(value);
  }

  stopTimer() {
    this.timerSub?.unsubscribe();
    this.time.next(null);
  }

  /**
   * Starts the countdown only for a cart that actually holds its lines for a limited time. The
   * backend sends `reserved_until: null` when the cart's POS has cart expiration switched off
   * (PAUL-3060), and a null leaves `time$` null so the header timer stays hidden. The cart is the
   * only signal needed — a POS with the setting off can still send a timestamp, for a cart holding
   * a movie show that cannot outlive its screening, and that one does count down.
   */
  startTimerIfLimited(reservedUntil: string | null, serverTime: string | null): void {
    if (!reservedUntil || !serverTime) {
      this.stopTimer();
      return;
    }
    // A hold that has already run out must not start a countdown: `count` would immediately flag a
    // timeout and pop the expired dialog over a cart the caller is already discarding (a refresh
    // with a stale `cart_id` restarts the flow instead), and the interceptor covers the rest.
    if (Date.parse(reservedUntil) - Date.parse(serverTime) <= 0) {
      this.stopTimer();
      return;
    }
    this.timerSub = this.count(reservedUntil, serverTime).subscribe();
  }

  getCartIdValue(): string {
    return this.cartId.value;
  }

  getUnfilteredBioscoopcards(): Observable<Bioscoopcard[]> {
    return this.boscoopcards.asObservable();
  }

  public getReservationName(integrationId: string): string {
    let reservationName = '';
    this.integrationReservations
      .find((res) => res.restaurant.id === integrationId)
      .restaurant.products.forEach((product) => (reservationName += `${product.name}`));
    return reservationName;
  }

  makeReservations(
    reservations: RestaurantReservation[],
    userData: ReservationUserData
  ): Observable<RestaurantReservation[]> {
    if (!reservations?.length) {
      return of([]);
    }
    return forkJoin(
      reservations.map((reservation) => {
        return this.reservingService.makeReservation(
          reservation.table_start,
          userData,
          reservation.notes,
          reservation.id
        );
      })
    ).pipe(map(() => reservations));
  }

  withReservation(): Observable<boolean> {
    return this.restaurantReservations$.pipe(
      map((reservations) => {
        return reservations?.length > 0;
      })
    );
  }

  requireReservation(): Observable<boolean> {
    return this.restaurantReservations$.pipe(
      map((reservations) => {
        return reservations && reservations.some((res) => res && !res.table_start);
      })
    );
  }

  addTicketWithSeat(seat: Seat, product: Product): Observable<Ticket> {
    return this.cancelAndClearPendingOrder().pipe(
      concatMap(() => this.reserveSeat(seat.id, product.id)),
      // Keyed on the same field `reserveSeat` gated on, so this response's own pass ties and applies
      // while an older one is dropped. The hold itself is per cart, so a stale answer would only
      // lengthen the countdown by the gap between the two reads — gated for consistency, not a fix.
      this.applyIfFresh<SeatsReservationResponse>(
        (res) => res.cart.server_time,
        (res) => this.startTimerIfLimited(res.reserved_until, res.server_time)
      ),
      map((res) => {
        return new Ticket(this.seatsState.getSeatData(seat.id), product, res.cart_line_id);
      }),
      tap((ticket) => this.setTickets([...this.tickets.value, ticket])),
      first()
    );
  }

  count(reservedUntilStr: string, serverTimeStr: string): Observable<number> {
    this.stopTimer();
    const reservedUntil = Date.parse(reservedUntilStr);
    const serverTime = Date.parse(serverTimeStr);
    const durationMs = reservedUntil - serverTime;
    const countTo = Date.now() + durationMs;
    return timer(0, 1000).pipe(
      map(() => countTo - Date.now()),
      tap((time: number) => {
        if (time <= 1010) {
          this.setTimedOut(true);
        }
        this.time.next(time);
      }),
      takeWhile(() => countTo - 1000 > Date.now())
    );
  }

  addTicketProduct(product: Product): Observable<Ticket> {
    const ticket = new Ticket(null, product);
    return this.cancelAndClearPendingOrder().pipe(
      concatMap(() => this.reserveProduct(product.id)),
      tap(({ reservationId }) => {
        ticket.setProductReservation(reservationId);
      }),
      tap(() => this.setTickets([...this.tickets.value, ticket])),
      tap(() => this.syncTicketSeatsFromLines()),
      map(() => ticket)
    );
  }

  addGiftCard(code: string): Observable<GiftCard> {
    const giftCard: GiftCard = { code };
    const pendingOrder = this._pendingOrder.value;

    if (pendingOrder) {
      return this.orderService.getGiftCardBalance(code).pipe(
        concatMap((card) => {
          if (card.balance <= 0) {
            return throwError(() => new Error(this.translocoService.translate(GIFTCARD_NO_BALANCE_ERROR)));
          }
          // The order's applied rows come back with a null balance, so this lookup is the only place
          // a typed card's balance is ever seen. Keep it, or the row cannot say how much is left.
          this.rememberGiftCardBalance(code, card.balance);
          const amount = Math.min(card.balance, this.pendingAmount.value);
          return this.orderService.addOrderPayment(pendingOrder.id, 'gift-card', code, undefined, amount);
        }),
        tap((order) => this.syncStateFromOrder(order)),
        map(() => giftCard)
      );
    }

    return this.cartsApiService.addGiftCard(this.cartId.value, giftCard).pipe(
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.giftCards.next(cart.summary.gift_cards);
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
        }
      ),
      map(() => giftCard)
    );
  }

  removeGiftCard(paymentId: string): Observable<CartDto | Order> {
    const pendingOrder = this._pendingOrder.value;

    if (pendingOrder) {
      return this.orderService.deleteOrderPayment(pendingOrder.id, paymentId).pipe(
        tap((order) => this.syncStateFromOrder(order)),
        catchError(() =>
          // Order status (e.g. fully covered by gift card) blocks payment removal — cancel and restore cart.
          this.paulyOrderService.cancelOrders({ orderIds: [pendingOrder.id] }).pipe(
            tap(() => this._pendingOrder.next(null)),
            concatMap(() => this.cartsApiService.getCart(this.cartId.value, { include: 'hold_blockers' })),
            this.applyIfFresh<CartDto>(
              (cart) => cart.server_time,
              (cart) => {
                this.giftCards.next(cart.summary.gift_cards ?? []);
                this.discounts.next(cart.summary.discounts ?? []);
                this.total.next(cart.summary.total_amount);
                this.pendingAmount.next(cart.summary.pending_amount);
              }
            )
          )
        )
      );
    }

    return this.cartsApiService.removeGiftCard(this.cartId.value, paymentId).pipe(
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.giftCards.next(cart.summary.gift_cards);
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
        }
      ),
      map((cart) => cart)
    );
  }

  /**
   * The applied gift cards of a pending order, assembled from the two halves the order reports them
   * in (PAUL-3203). Neither half is enough on its own:
   *
   * - `payments` carries the payment id — which is what `removeGiftCard` deletes by — and
   *   `can_delete`. Its `payment_method_name` is the *payment integration* ("Film- en Ristobarbon"),
   *   not the gift-card product, and its `code` is the card's real redeemable code.
   * - `summary.gift_cards` carries the gift-card product's name ("Film-Ristobarbon"), which is what
   *   a row is titled with. Its `id` is neither the payment's nor the card's, so it is never used;
   *   `code` is the only reliable key between the two.
   *
   * `balance` comes back null here — the backend serves it on the cart's apply response, not the
   * order's — so a caller wanting it must read it from the connected-cards listing or from
   * `getGiftCardBalance`.
   */
  private giftCardsFromOrderPayments(order: Order): GiftCard[] {
    const productNamesByCode = new Map(
      (order.summary.gift_cards ?? []).filter((card) => !!card.code).map((card) => [card.code, card.name])
    );
    return (order.summary.payments ?? [])
      .filter((p: OrderPayment) => p.payment_method_external_id === 'gift-card')
      .map((p: OrderPayment) => ({
        id: p.id,
        code: p.code,
        name: productNamesByCode.get(p.code) ?? p.payment_method_name,
        amount: p.amount,
        can_delete: p.can_delete,
        balance: this.giftCardBalances.get(p.code) ?? null,
      }));
  }

  private syncStateFromOrder(order: Order): void {
    const existingPendingOrder = this._pendingOrder.value;
    this.total.next(order.summary.total_amount);
    this.pendingAmount.next(order.summary.pending_amount);
    this.discounts.next(order.summary.discounts ?? []);
    this.giftCards.next(this.giftCardsFromOrderPayments(order));
    this._pendingOrder.next({
      ...order,
      payment_url: order.summary.payments?.find((p) => p.payment_url)?.payment_url ?? order.payment_url,
      payment_methods: existingPendingOrder?.payment_methods ?? [],
    });
    const orderPaymentsById = new Map((order.summary.payments ?? []).map((p) => [p.id, p]));
    this.boscoopcards.next(
      this.boscoopcards.value
        .filter((card) => orderPaymentsById.has(card.id))
        .map((card) => {
          const payment = orderPaymentsById.get(card.id);
          return { ...card, applied_amount: payment.amount, can_delete: payment.can_delete };
        })
    );
  }

  refreshPendingOrder(orderId: string): Observable<Order> {
    return this.orderService.getOrder(orderId).pipe(tap((order) => this.syncStateFromOrder(order)));
  }

  recoverStateFromOrder(order: Order): void {
    this.total.next(order.summary.total_amount);
    this.pendingAmount.next(order.summary.pending_amount);
    this.discounts.next(order.summary.discounts ?? []);
    this.giftCards.next(this.giftCardsFromOrderPayments(order));

    this._pendingOrder.next({
      ...order,
      payment_url: order.summary.payments?.find((p) => p.payment_url)?.payment_url ?? order.payment_url,
      payment_methods: order?.payment_methods ?? [],
    });
    const orderPaymentsById = new Map((order.summary.payments ?? []).map((p) => [p.id, p]));
    console.log(order.summary.payments);
    const cards = order.summary.payments
      .filter((payment) => payment.payment_method_external_id === 'BIOSCOOPCARD')
      .map((card: OrderPayment) => {
        return {
          ...card,
          id: card.id,
          applied_amount: card.amount,
          can_delete: card.can_delete,
          code: card.code,
        };
      });
    this.boscoopcards.next(cards);
  }

  addDiscount(code: string): Observable<CartDiscountCode> {
    const pendingOrder = this._pendingOrder.value;

    if (pendingOrder) {
      return this.orderService
        .addOrderDiscount(pendingOrder.id, code, new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true))
        .pipe(
          tap((order) => this.syncStateFromOrder(order)),
          map((order) => (order.summary.discounts ?? []).find((d) => d.code === code))
        );
    }

    return this.cartsApiService.addDiscount(this.cartId.value, code).pipe(
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.discounts.next(cart.summary.discounts ?? []);
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
          this.setLines(cart.summary.lines);
          this.setRowsFromSummary(cart.summary);
        }
      ),
      map((cart) => (cart.summary.discounts ?? []).find((d) => d.code === code))
    );
  }

  removeDiscount(discountCodeId: string): Observable<CartDto | Order> {
    const pendingOrder = this._pendingOrder.value;

    if (pendingOrder) {
      return this.orderService
        .removeOrderDiscount(pendingOrder.id, discountCodeId)
        .pipe(tap((order) => this.syncStateFromOrder(order)));
    }

    return this.cartsApiService.removeDiscount(this.cartId.value, discountCodeId).pipe(
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.discounts.next(cart.summary.discounts ?? []);
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
          this.setLines(cart.summary.lines);
          this.setRowsFromSummary(cart.summary);
        }
      )
    );
  }

  addBioscoopcard(code: string, pin: string, externalId: string): Observable<CartDto | Order> {
    const pendingOrder = this._pendingOrder.value;

    //if (pendingOrder) { order is created on payment step, so bioscoopcard can only be added to existing order
    return this.orderService.getExternalGiftCardBalance(this.context.posId, code, pin || undefined).pipe(
      concatMap((card) => {
        if (card.status === 'Expired') {
          return throwError(() => new Error(this.translocoService.translate(BIOSCOOPCARD_EXPIRED_ERROR)));
        }
        if (card.balance <= 0) {
          return throwError(() => new Error(this.translocoService.translate(BIOSCOOPCARD_NO_BALANCE_ERROR)));
        }
        const amount = Math.min(card.balance, this.pendingAmount.value);
        return this.orderService.addOrderPayment(pendingOrder.id, externalId, code, pin, amount).pipe(
          tap((order) => {
            // Register the new card with available_amount from the balance check before syncing,
            // so syncStateFromOrder can preserve it (available_amount is not in the order response).
            const existingIds = new Set(this.boscoopcards.value.map((b) => b.id));
            const payment = order.summary.payments?.find(
              (p) => p.payment_method_external_id === externalId && !existingIds.has(p.id)
            );
            if (payment) {
              this.boscoopcards.next([
                ...this.boscoopcards.value,
                {
                  id: payment.id,
                  code,
                  applied_amount: payment.amount,
                  can_delete: payment.can_delete,
                },
              ]);
            }
            this.syncStateFromOrder(order);
          })
        );
      })
    );
    //}

    // return this.cartsApiService.addBioscoopcard(this.cartId.value, code, pin, externalId).pipe(
    //   tap((cart) => this.boscoopcards.next(cart.summary.boscoopcards ?? [])),
    //   tap((cart) => this.total.next(cart.summary.total_amount)),
    //   tap((cart) => this.pendingAmount.next(cart.summary.pending_amount)),
    //   tap((cart) => this.discounts.next(cart.summary.discounts ?? []))
    // );
  }

  removeBioscoopcard(cardId: string): Observable<CartDto | Order> {
    const pendingOrder = this._pendingOrder.value;

    if (pendingOrder) {
      return this.orderService
        .deleteOrderPayment(pendingOrder.id, cardId)
        .pipe(tap((order) => this.syncStateFromOrder(order)));
    }

    return this.cartsApiService.removeBioscoopcard(this.cartId.value, cardId).pipe(
      // Keyed on the card this call removed, not on the answer, so it stays outside the stale guard:
      // nothing else repaints `boscoopcards` from a cart summary, and a dropped answer would leave
      // the removed card on screen for good.
      tap(() => this.boscoopcards.next(this.boscoopcards.value.filter((b) => b.id != cardId))),
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
        }
      )
    );
  }

  assignSeatToTicket(seatId: string, seatType: string): Observable<Ticket> {
    const assignableTickets = this.tickets.value.filter((t) => t.seat === null);
    const ticketToAssign = assignableTickets[this.currentTicketsInReserveringPhase - 1];
    return this.cancelAndClearPendingOrder().pipe(
      concatMap(() => this.reserveSeat(seatId, ticketToAssign.product?.id, ticketToAssign.reservationId)),
      tap(() => {
        ticketToAssign.assignSeat(this.seatsState.getSeatData(seatId));
      }),
      tap(() => this.updateTickets(ticketToAssign)),
      map(() => ticketToAssign),
      first()
    );
  }

  tryToReinitializeCart(cartIdToReinit: string | null): Observable<{
    cart: CartDto;
    products: Product[];
    restaurantReservations: RestaurantReservationModel;
  } | null> {
    if (!cartIdToReinit) {
      return of(null);
    }

    return this.cartsApiService.getCart(cartIdToReinit).pipe(
      switchMap((cart) => {
        if (cart.status === 'expired') {
          return of(null);
        }
        const showId = cart?.summary?.lines?.[0]?.movie_show_id;
        if (!showId) {
          return of(null);
        }
        return forkJoin({
          productsByShow: this.productsService.getProductsByShow(showId, cart.pos_id),
          addons: this.productsService.getAddonsByPosId(cart.pos_id),
          restaurantReservations:
            of(
              null
            ) /* for restaurant support this.reservingService.getReservation(cart.id).pipe(catchError((err) => of(null))), */,
        }).pipe(
          map(({ productsByShow, addons, restaurantReservations }) => ({
            cart,
            products: [...productsByShow, ...addons],
            restaurantReservations,
          }))
        );
      })
    );
  }
  /**
   * Reserves one subscription product, creating/extending the cart exactly like a ticket
   * reservation (same reservations endpoint + setCartId), so the shared payment step can build
   * the order from it. Returns the created cart line id.
   */
  reserveSubscription(productId: string, billingPeriodId: string | null): Observable<string> {
    this.invalidatePendingOrder();
    this.reserving.next(true);
    return this.subscriptionShopService
      .addToCart(productId, this.context.posId, this.cartId.value, billingPeriodId)
      .pipe(
        tap((res) => this.setCartId(res.cartId)),
        this.applyIfFresh(
          (res) => res.serverTime,
          (res) => {
            this.total.next(res.total);
            this.pendingAmount.next(res.total);
            this.totalRecurringMonthly.next(res.totalRecurringMonthly);
            // Keeps the summary's setup-fee breakdown live: the fee is only ever reported per line.
            this.setLines(res.lines);
            this.requiresAccount.next(res.requiresAccount);
            this.startTimerIfLimited(res.reservedUntil, res.serverTime);
          }
        ),
        map((res) => res.cartLineId),
        finalize(() => this.reserving.next(false)),
        take(1)
      );
  }

  /**
   * Reserves one gift card at `amount`, extending the same cart the subscription lines live in so a
   * prepaid order can hold gift cards and non-recurring subscriptions together. Returns the cart
   * line id. `amount` is one of the product's predefined amounts, or null for a gift card priced
   * normally — never a value of the caller's own making (PAUL-3071).
   */
  reserveGiftCard(productId: string, amount: number | null): Observable<string> {
    this.invalidatePendingOrder();
    this.reserving.next(true);
    return this.productsService.makeShowlessReservation(productId, this.context.posId, this.cartId.value, amount).pipe(
      tap((cartStatus) => this.setCartId(cartStatus.id)),
      this.applyIfFresh<CartStatus>(
        (cartStatus) => cartStatus.server_time,
        (cartStatus) => {
          this.total.next(cartStatus.total);
          this.pendingAmount.next(cartStatus.pending_amount);
          this.totalRecurringMonthly.next(cartStatus.total_recurring_monthly);
          this.setLines(cartStatus.lines);
          this.requiresAccount.next(cartStatus.requiresAccount);
          this.startTimerIfLimited(cartStatus.reserved_until, cartStatus.server_time);
        }
      ),
      map((cartStatus) => cartStatus.reservationId),
      finalize(() => this.reserving.next(false)),
      take(1)
    );
  }

  /**
   * Removes a single showless cart line — a subscription or a gift card — through the same
   * reservations endpoint used to add it, keeping the running total in sync.
   * Mirrors {@link reserveSubscription} and {@link reserveGiftCard}.
   */
  removeShowlessLine(cartLineId: string): Observable<void> {
    this.invalidatePendingOrder();
    this.reserving.next(true);
    // These lines have no show, so show_id stays null (the throwing context.showId getter is avoided).
    return this.productsService.removeReservation(cartLineId, this.context.posId, this.context.get().showId).pipe(
      tap((cartStatus) => this.setCartId(cartStatus.id)),
      this.applyIfFresh<CartStatus>(
        (cartStatus) => cartStatus.server_time,
        (cartStatus) => {
          this.total.next(cartStatus.total);
          this.pendingAmount.next(cartStatus.pending_amount);
          this.totalRecurringMonthly.next(cartStatus.total_recurring_monthly);
          this.setLines(cartStatus.lines);
          this.requiresAccount.next(cartStatus.requiresAccount);
          // An emptied cart holds nothing, so it must not keep counting down (mirrors the ticket flow).
          if (!cartStatus.lines.length) {
            this.stopTimer();
          }
        }
      ),
      map(() => undefined),
      finalize(() => this.reserving.next(false)),
      take(1)
    );
  }

  /**
   * Re-hydrates cart-level state (id, status, totals, lines) from a persisted cart. Used by the
   * subscription checkout to survive a page refresh without re-reserving. Does not navigate, since
   * the cart_id is already in the URL on reinit.
   */
  reinitSubscriptionCart(cartId: string): Observable<CartDto> {
    return this.cartsApiService.getCart(cartId).pipe(
      tap((cart) => {
        this.cartId.next(cart.id);
        // A restore, not an incremental paint — always applies, only raises the watermark (see `setCart`).
        this.acceptCartView(cart.server_time);
        this.status = cart.status;
        this.total.next(cart.summary.total_amount);
        this.pendingAmount.next(cart.summary.pending_amount);
        this.totalRecurringMonthly.next(cart.summary.total_recurring_monthly ?? 0);
        this.setLines(cart.summary.lines);
        // The orders this cart has already produced. Two things need them, and both only ever run
        // after a restore: the payment step recovers its pending order from them instead of posting a
        // second one, and SubscriptionFlowAccess reads them as the record that the buyer reached the
        // payment step at all. The ticket flow restores them the same way (see `setCart`).
        this._cartOrders.next(cart.summary.orders ?? []);
        // Restored before the products are, so this is what the account step goes on after a refresh.
        this.requiresAccount.next(cartRequiresAccount(cart.summary.products));
        // A refresh must not hand the buyer a fresh full period: the countdown resumes against the
        // cart's own `reserved_until`, and stays off entirely for a cart with no time limit. Only an
        // active cart still counts down — `subs-order/:orderId` rehydrates its cart through here too,
        // and an ordered one must not expire under a confirmed order.
        if (cart.status === CartLifecycle.Active) {
          this.startTimerIfLimited(cart.reserved_until, cart.server_time);
        } else {
          this.stopTimer();
        }
      }),
      take(1)
    );
  }

  /**
   * Re-reads what the cart costs — totals, per-month figure and the lines the summary breaks down —
   * without touching the rest of the restored state (status, orders, countdown).
   *
   * Only the backend knows what a subscription's first term comes to: a periodic plan is pro-rated
   * from its start date, carries its setup fee and has its discount applied, so every amount the
   * reservation returned is stale as soon as the confirmation step writes a start date or a billing
   * period onto a line. Called from there, so the payment step shows the amount that is about to be
   * debited rather than the plan's sticker price.
   */
  refreshCartMoney(): Observable<CartDto | null> {
    const cartId = this.cartId.value;
    if (!cartId) {
      return of(null);
    }
    return this.cartsApiService.getCart(cartId).pipe(
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
          this.totalRecurringMonthly.next(cart.summary.total_recurring_monthly ?? 0);
          this.setLines(cart.summary.lines);
        }
      ),
      take(1)
    );
  }

  private setCartId(cartId: string) {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.router.navigate([], {
      queryParams: { ...queryParams, cart_id: cartId },
      queryParamsHandling: 'merge',
    });
    this.cartId.next(cartId);
  }
  unassignSeatFromTicket(seatId: string): Observable<Ticket> {
    const tickets = [...this.tickets.value];
    const ticket = tickets.find((t) => t.seat?.id === seatId);
    return this.cancelAndClearPendingOrder().pipe(
      concatMap(() => this.removeSeatReservation(ticket.reservationId, true)),
      map(() => ticket),
      tap(() => ticket.unassignSeat()),
      tap(() => this.updateTickets(ticket)),
      first()
    );
  }

  changeTicketProduct(ticket: Ticket, product: Product): Observable<Ticket> {
    let action: Observable<CartStatus> = of(null);

    if (ticket.hasProductReservation()) {
      action = this.removeProductResevation(ticket.reservationId);
    }

    return action.pipe(
      concatMap(() => this.reserveProduct(product.id, null, ticket.seat.id)),
      tap(() => ticket.assignProduct(product)),
      tap((res) => ticket.setProductReservation(res.reservationId)),
      tap(() => this.setTickets([...this.tickets.value])),
      map(() => ticket),
      first()
    );
  }

  /**
   * The cards redeemable on each subscription-ticket line of the current cart (PAUL-3557). Empty
   * before a cart exists — the call needs its id, and a guest cart answers with every line empty.
   */
  getSubscriptionCards(): Observable<CartSubscriptionCardLineDto[]> {
    const cartId = this.getCartIdValue();
    return cartId ? this.cartsApiService.getSubscriptionCards(cartId) : of([]);
  }

  /**
   * The gift cards connected to the customer's account that can be spent on this cart (PAUL-3203).
   * Empty before a cart exists, and empty for a guest — the endpoint answers `[]` rather than
   * refusing, so nothing here has to know whether the session is signed in.
   *
   * Deliberately a call rather than a BehaviorSubject: it is re-read after every apply and removal,
   * and holding it would only add a second thing to keep in step with {@link giftCards$}. A failing
   * request yields an empty list — losing the offer must not take the gift-card step down with it,
   * since a code can still be typed by hand.
   */
  getConnectedGiftCards(): Observable<GiftCardCartModel[]> {
    const cartId = this.getCartIdValue();
    if (!cartId) {
      return of([]);
    }
    return this.cartsApiService.getGiftCards(cartId).pipe(
      tap((cards) => cards.forEach((card) => this.rememberGiftCardBalance(card.code, card.balance))),
      catchError(() => of([] as GiftCardCartModel[]))
    );
  }

  /** @see giftCardBalances */
  private rememberGiftCardBalance(code: string | null | undefined, balance: number | null | undefined): void {
    if (code && typeof balance === 'number') {
      this.giftCardBalances.set(code, balance);
    }
  }

  /**
   * Redeems a subscription card on a line already in the cart, or detaches it when `code` is empty
   * (PAUL-2844). The card is optional on `POST /reservations`, so a flagged ticket goes into the
   * cart first and is redeemed here — the line keeps its id, unlike the delete-and-re-issue this
   * replaces. Re-sending the same code is safe, which is what makes a retry (or a refresh) cheap.
   * The cart is re-read afterwards because redeeming a card changes what the line costs and the
   * endpoint echoes only the card itself back.
   */
  setSubscriptionCard(cartLineId: string, code: string | null): Observable<SubscriptionCardAttachDto> {
    const trimmedCode = (code ?? '').trim();
    return this.cancelAndClearPendingOrder().pipe(
      concatMap(() =>
        this.productsService.setSubscriptionCard(cartLineId, {
          shop_id: this.context.get().shopId,
          ...(trimmedCode ? { subscription_card_code: trimmedCode } : {}),
        })
      ),
      tap((res) => this.patchLineSubscriptionCard(cartLineId, res.subscription_card_code)),
      concatMap((res) => this.refreshCart().pipe(map(() => res))),
      take(1)
    );
  }

  /**
   * Ticket lines whose product is flagged `is_subscription_ticket` and still carry no card. These
   * are exactly what `POST /orders` refuses, so the checkout button waits for this to run empty
   * instead of relying on the 400 (PAUL-2844). Synchronous, because the information step answers it
   * from a click handler and from its disabled-button check.
   */
  getCardlessSubscriptionTickets(): Ticket[] {
    return this.tickets.value.filter(
      (ticket) =>
        ticket.product?.is_subscription_ticket && !this.lineHasSubscriptionCard(this.lines.value, ticket.reservationId)
    );
  }

  private lineHasSubscriptionCard(lines: CartSummaryLineDto[], cartLineId: string): boolean {
    return !!lines.find((line) => line.id === cartLineId)?.subscription_card_code;
  }

  /**
   * Publishes cart lines, carrying over a subscription card this client attached when the incoming
   * line reports none at all. An explicit `null` is a detach and always wins — only a missing field
   * falls back, so a backend that does not echo `subscription_card_code` on the cart summary cannot
   * leave the buyer stuck behind the checkout gate on a line they did redeem.
   */
  private setLines(lines: CartSummaryLineDto[]): void {
    const previous = new Map(this.lines.value.map((line) => [line.id, line]));
    this.lines.next(
      lines.map((line) => {
        const carriedOver = previous.get(line.id)?.subscription_card_code;
        return line.subscription_card_code === undefined && carriedOver
          ? { ...line, subscription_card_code: carriedOver }
          : line;
      })
    );
  }

  private patchLineSubscriptionCard(cartLineId: string, code: string | null): void {
    this.lines.next(
      this.lines.value.map((line) => (line.id === cartLineId ? { ...line, subscription_card_code: code } : line))
    );
  }

  /**
   * Re-reads the cart after a line-level change whose endpoint does not hand the cart back. A failed
   * refresh is not a failed change: the card is attached either way, so the caller still gets its
   * success and the totals catch up on the next cart-mutating call.
   */
  private refreshCart(): Observable<CartDto | null> {
    const cartId = this.cartId.value;
    if (!cartId) {
      return of(null);
    }
    return this.cartsApiService.getCart(cartId).pipe(
      this.applyIfFresh<CartDto>(
        (cart) => cart.server_time,
        (cart) => {
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
          this.discounts.next(cart.summary.discounts ?? []);
          this.setLines(cart.summary.lines);
          this.setRowsFromSummary(cart.summary);
        }
      ),
      catchError(() => of(null))
    );
  }

  removeTicketBySeat(seatId: string): Observable<Ticket> {
    const ticket = this.tickets.value.find((t) => t.seat?.id === seatId);
    if (ticket) {
      this.setTickets(this.tickets.value.filter((t) => t !== ticket));

      return this.cancelAndClearPendingOrder().pipe(
        concatMap(() => this.removeSeatReservation(ticket.reservationId)),
        map(() => ticket)
      );
    }
    return of(null);
  }

  removeTicketByProductId(productId: string): Observable<Ticket | null> {
    const ticketsBefore = this.tickets.value.length;
    const totalBefore = this.total.value;
    this.clarityService.set('cart_remove_productId', productId);
    this.clarityService.set('cart_remove_ticketsBefore', String(ticketsBefore));
    this.clarityService.set('cart_remove_totalBefore', String(totalBefore));
    this.clarityService.set('cart_remove_reserving', String(this.reserving.value));

    const ticketIndex = this.tickets.value.findIndex((t) => t.product?.id === productId);
    if (ticketIndex > -1) {
      const ticket = this.tickets.value[ticketIndex];
      this.clarityService.set('cart_remove_ticketIndex', String(ticketIndex));
      this.clarityService.set('cart_remove_reservationId', String(ticket.reservationId));
      this.clarityService.set('cart_remove_hasReservation', String(ticket.hasProductReservation()));

      /* TODO: ticket removal should be after successful endpoint*/ this.setTickets(
        this.tickets.value.filter((t, i) => i !== ticketIndex)
      );
      if (ticket.hasProductReservation()) {
        this.clarityService.event('cart_remove_api_call');
        this.reserving.next(true);
        return this.cancelAndClearPendingOrder().pipe(
          concatMap(() => this.removeProductResevation(ticket.reservationId)),
          tap(() => this.syncTicketSeatsFromLines()),
          concatMap((cartStatus) => this.seatsState.fetchTaken(this.showState.show.id).pipe(map(() => cartStatus))),
          tap(() => {
            this.clarityService.set('cart_remove_totalAfter', String(this.total.value));
            this.clarityService.event('cart_remove_api_success');
          }),
          map(() => ticket),
          catchError((err: HttpErrorResponse) => {
            this.clarityService.set('cart_remove_errorStatus', String(err?.status));
            this.clarityService.set('cart_remove_errorMessage', String(err?.message).substring(0, 255));
            this.clarityService.event('cart_remove_api_error');
            this.setTickets([...this.tickets.value, ticket]);
            return throwError(() => err);
          }),
          finalize(() => this.reserving.next(false))
        );
      }

      this.clarityService.event('cart_remove_no_reservation');
      return this.cancelAndClearPendingOrder().pipe(map(() => ticket));
    }

    this.clarityService.event('cart_remove_no_match');
    return of(null);
  }

  private getParentCartLineId(tickets: Ticket[], parentId: string): string | null {
    const items = this.lines.value;
    const parents = items.filter((i) => i.parent_cart_line_id).map((i) => i.parent_cart_line_id);

    const parent = tickets.reverse().find((i) => i.product.id === parentId && !parents.includes(i.reservationId));
    if (parent) {
      return parent?.reservationId ?? null;
    }

    return null;
  }

  addItem(supplement: Product, supplements: Product[], parentId = null): Observable<Supplement> {
    const items = this.items.value;
    const tickets = this.tickets.value;
    const mutuallyExclusiveProducts = supplements.filter((s) => s.isMutuallyExclusive);

    if (
      supplement.isMutuallyExclusive &&
      this.hasDifferentMutuallyExclusiveProductInCart(items, mutuallyExclusiveProducts, supplement)
    ) {
      return throwError(() => new Error(this.translocoService.translate(MANUALLY_EXCLUSIVE_ERROR)));
    }
    const parentLineId = this.getParentCartLineId(tickets, parentId);
    return this.cancelAndClearPendingOrder().pipe(
      concatMap(() => this.reserveProduct(supplement.id, parentLineId)),
      map(() => supplement)
    );
  }

  removeItem(productId: string): Observable<string> {
    const cartLineId = this.popCartLine(productId).id;
    if (cartLineId) {
      this.reserving.next(true);
      return this.cancelAndClearPendingOrder().pipe(
        concatMap(() => this.removeProductResevation(cartLineId)),
        map(() => cartLineId),
        finalize(() => this.reserving.next(false))
      );
    }
    return of(null);
  }

  private hasDifferentMutuallyExclusiveProductInCart(
    items: CartItem[],
    mutuallyExclusiveProducts: Supplement[],
    supplement: Supplement
  ): boolean {
    return items.some(
      (item) =>
        item.productId !== supplement.id &&
        mutuallyExclusiveProducts.some((exclusiveProduct) => exclusiveProduct.id === item.productId)
    );
  }

  private popCartLine(productId: string): CartSummaryLineDto | null {
    const items = this.items.value;
    /* if the same supplement exists in other product combi then this wont always
     remove it because there are multiple items with this productId, not fixing because management said its not real life scenario */
    const item = items.find((i) => i.productId === productId);
    if (item) {
      return item.lines.pop();
    }

    return null;
  }

  canAddMore(product: Product): Observable<boolean> {
    return combineLatest([this.items$, this.tickets$]).pipe(
      map(([items, tickets]) => {
        const item = items.find((i) => i.productId === product.id);

        return !item || !product.isLimitedToMainProduct || item.quantity < tickets.length;
      })
    );
  }

  getItemQuantity(productId: string): Observable<number> {
    return this.items$.pipe(map((items) => items.find((i) => i.productId === productId)?.quantity ?? 0));
  }

  getSupplementQuantity(suppId: string, parentProductId: string): Observable<number> {
    /* return this.items$.pipe(map((items) => items.find((i) => i.productId === suppId)?)); */
    return this.items$.pipe(
      map((items) => {
        return items.find((i) => i.productId === suppId)?.quantity ?? 0;
      })
    );
  }

  getItemMaxQuantity(supplement: Supplement): Observable<number> {
    return supplement.isLimitedToMainProduct ? this.getTicketQuantity(supplement.mainProductId) : of(null);
  }

  getTicketQuantity(productId: string): Observable<number | null> {
    return this.tickets$.pipe(map((tickets) => tickets.filter((t) => t.product?.id === productId).length ?? 0));
  }

  reserveSeat(seatId: string, productId: string, parentCartLineId = null): Observable<SeatsReservationResponse | null> {
    if (this.isOrderCreated) {
      return EMPTY;
    }
    const { posId, shopId, showId, areaId } = this.context.get();

    this.reserving.next(true);

    return this.seatsService
      .reserveSeat(
        { seatId, productId },
        shopId,
        showId,
        areaId,
        this.cartId.value,
        posId,
        parentCartLineId,
        this.shopSessionService.getSessionId()
      )
      .pipe(
        tap((res) => this.setCartId(res.cart_id)),
        this.applyIfFresh<SeatsReservationResponse>(
          (res) => res.cart.server_time,
          (res) => {
            this.total.next(res.cart.summary.total_amount);
            this.pendingAmount.next(res.cart.summary.pending_amount);
          }
        ),
        finalize(() => this.reserving.next(false)),

        take(1)
      );
  }

  private updateTickets(ticket: Ticket): void {
    const tickets = [...this.tickets.value];
    const index = tickets.findIndex((t) => t === ticket);
    if (index > -1) {
      tickets[index] = ticket;
    }
    this.setTickets(tickets);
  }

  private reserveProduct(
    productId: string,
    parentCartLineId: string = null,
    seatId: string = null
  ): Observable<CartStatus | null> {
    if (this.isOrderCreated) {
      return EMPTY;
    }
    const posId = this.context.posId;
    const showId = this.context.showId;

    const waitingForCart = !this.getCartIdValue() && this.getReservingValue();
    if (waitingForCart) {
      return EMPTY;
    }
    this.reserving.next(true);
    return this.cartId$.pipe(
      concatMap((cartId) =>
        this.productsService
          .makeReservation(
            productId,
            cartId,
            posId,
            showId,
            parentCartLineId,
            seatId,
            this.shopSessionService.getSessionId()
          )
          .pipe(
            this.applyIfFresh<CartStatus>(
              (cartStatus) => cartStatus.server_time,
              (cartStatus) => {
                this.startTimerIfLimited(cartStatus.reserved_until, cartStatus.server_time);
                this.updateCart(cartStatus);
              }
            )
          )
      ),
      take(1),
      finalize(() => this.reserving.next(false))
    );
  }

  getPaymentMethods(): Observable<PaymentMethodViewData[]> {
    const pendingOrder = this._pendingOrder.value;
    const items$ = pendingOrder
      ? of(pendingOrder.payment_methods)
      : this.cartId$.pipe(
          concatMap((cartId) => this.orderService.getPaymentMethods(cartId)),
          first()
        );
    return items$.pipe(
      catchError(() => of(null)),
      map((items) => {
        if (!items) return null;
        return items.map((item) => ({
          id: item.external_id,
          externalId: item.external_id,
          name: item.name,
          img: item.photo?.url1x,
          fee: item.cost_total,
          integrationType: item.integration_type ?? null,
          variants: item.variants,
          acceptsCode: item.accepts_code,
          acceptsPin: item.accepts_pin,
        }));
      })
    );
  }

  createPendingOrder(
    userData: ReservationUserData
  ): Observable<Order & { payment_url: string; payment_methods: PaymentMethod[] }> {
    if (this._pendingOrder.value && !this._pendingOrderDirty) {
      this.pendingAmount.next(this._pendingOrder.value.summary.pending_amount);
      return of(this._pendingOrder.value);
    }

    const requestBody: OrderRequest = {
      cart_id: this.cartId.value,
      buyer: {
        email: userData.email,
        first_name: userData.firstname,
        last_name: userData.lastname,
        // Absent means null, never '' — an empty string is a value the backend has to judge (FE-1).
        phone: userData.phoneNumber || null,
        language: this.translocoService.getActiveLang() || 'en',
        newsletter: userData.newsletter ? userData.newsletter : null,
      },
      payment_method_external_id: null,
      payment_method_variant_external_id: null,
      delivery: null,
      comment: null,
      pay_after: false,
      timeslot: null,
    };

    return this.orderService.createOrder(requestBody, { include: 'hold_blockers,payment_methods' }).pipe(
      map((response) => ({
        ...response,
        payment_url: response.summary.payments?.find((p) => p.payment_url)?.payment_url ?? response.payment_url,
        payment_methods: (response as any).payment_methods ?? [],
      })),
      tap((order) => {
        // Cleared on success only: a POST that failed leaves the held order still outdated, and the
        // retry has to post again rather than hand that one back as if it matched the cart.
        this._pendingOrderDirty = false;
        this._pendingOrder.next(order);
        this.pendingAmount.next(order.summary.pending_amount);
      })
    );
  }

  makeOrder(
    methodId: string | null,
    variantId: string | null,
    userData: ReservationUserData
  ): Observable<Order & { payment_url: string }> {
    const requestBody: OrderRequest = {
      cart_id: this.cartId.value,
      buyer: {
        email: userData.email,
        first_name: userData.firstname,
        last_name: userData.lastname,
        // Absent means null, never '' — an empty string is a value the backend has to judge (FE-1).
        phone: userData.phoneNumber || null,
        language: this.translocoService.getActiveLang() || 'en',
        newsletter: userData.newsletter ? userData.newsletter : null, // we are disabling potential cancellation of newsletter subscription
      },
      payment_method_external_id: methodId,
      payment_method_variant_external_id: variantId,
      delivery: null,
      comment: null,
      pay_after: false,
      timeslot: null,
    };

    return this.orderService.createOrder(requestBody).pipe(
      map((response) => {
        if (response.status === OrderStatus.Paid) {
          return response;
        }
        return {
          ...response,
          payment_url: response.summary.payments?.find((p) => p.payment_url)?.payment_url ?? response.payment_url,
        };
      })
    );
  }

  /**
   *  TODO: We decided at this moment to hardcode the delivery method to digital, since we don't have different delivery methods yet.
   *  But this should be changed in the future to allow the user to choose the delivery method.
   * */
  setDeliveryMethod(deliveryMethod = DeliveryType.Digital): Observable<void> {
    if (this.deliveryMethod.value) {
      return of(undefined);
    }
    return this.cartsService.setDeliveryMethod(deliveryMethod, this.cartId.value).pipe(
      tap(() => this.deliveryMethod.next(deliveryMethod)),
      first()
    );
  }

  regenerateDeliveryMethod(deliveryMethod: DeliveryType): void {
    this.deliveryMethod.next(deliveryMethod);
  }

  setExpiredError(): void {
    this.expiredError.next(true);
  }

  setIntegrationReservations(integrationReservations: IntegrationReservation[]) {
    this._integrationReservations.next(integrationReservations);
  }

  refreshHoldBlockers(): void {
    const cartId = this.getCartIdValue();
    const pendingOrder = this._pendingOrder.value;
    if (!cartId || !pendingOrder) return;

    this.cartsApiService
      .getHoldBlockers(cartId)
      .pipe(
        first(),
        catchError(() => EMPTY)
      )
      .subscribe((holdBlockers) => {
        this._pendingOrder.next({ ...this._pendingOrder.value, hold_blockers: holdBlockers });
      });
  }

  private removeSeatReservation(reservationId: string, onlySeatReservation = false): Observable<CartDto> {
    const { posId } = this.context.get();

    this.reserving.next(true);

    return this.seatsService.removeReservation(reservationId, posId, onlySeatReservation).pipe(
      this.applyIfFresh<{ cart: CartDto }>(
        ({ cart }) => cart.server_time,
        ({ cart }) => {
          this.total.next(cart.summary.total_amount);
          this.pendingAmount.next(cart.summary.pending_amount);
        }
      ),
      map(({ cart }) => cart),
      finalize(() => this.reserving.next(false))
    );
  }

  private removeProductResevation(reservationId: string): Observable<CartStatus> {
    const { posId, showId } = this.context.get();
    return this.productsService.removeReservation(reservationId, posId, showId).pipe(
      tap(() => {
        if (this.tickets.value.length === 0) {
          this.stopTimer();
        }
      }),
      this.applyIfFresh<CartStatus>(
        (cartStatus) => cartStatus.server_time,
        (cartStatus) => this.updateCart(cartStatus)
      )
    );
  }

  /**
   * Suppresses the state paint for a cart view older than the one already on screen (PAUL-3530).
   *
   * Every cart mutation answers with the whole cart, and the shop does not wait for one answer
   * before firing the next. HTTP guarantees no ordering, so remove A / remove B can answer B then A
   * and the older view paints last — the line reappears even though the backend released it. Each
   * payload carries `server_time`, stamped when the backend read the cart under the per-cart lock,
   * so a later value is by construction a later view of that cart.
   *
   * Gates the paint, not the emission: an equally stale response still reaches the subscriber, which
   * is what closes dialogs, assigns the seat in `assignSeatToTicket` and resolves the caller's `map`.
   * Filtering it out instead would abort those.
   *
   * Ties paint. Two reads stamped the same millisecond are ordered no better than they are today,
   * whereas dropping them would discard a genuinely newer cart for good.
   */
  private applyIfFresh<T>(serverTimeOf: (value: T) => string | null, apply: (value: T) => void) {
    return tap((value: T) => {
      if (this.acceptCartView(serverTimeOf(value))) {
        apply(value);
      }
    });
  }

  /**
   * True when this cart view may be painted, advancing the watermark as a side effect.
   * Split out of {@link applyIfFresh} for the restores ({@link setCart}, `restoreCart`), which paint
   * from a plain method call rather than from a stream and only ever raise the watermark.
   */
  private acceptCartView(serverTimeIso: string | null): boolean {
    const serverTime = serverTimeIso ? Date.parse(serverTimeIso) : NaN;
    if (serverTime < this.lastAppliedServerTime) {
      return false;
    }
    // A missing or unparseable stamp must not poison the watermark and lock every later response
    // out — such a response paints, exactly as it did before the guard existed.
    if (!Number.isNaN(serverTime)) {
      this.lastAppliedServerTime = serverTime;
    }
    return true;
  }

  /**
   * Paints a whole cart view. Both product reservation directions land here, and **both gate their
   * own call site** with {@link applyIfFresh} — guarding again in here would spend the watermark
   * twice on one response and leave the paint hanging on ties being accepted.
   */
  private updateCart(cartStatus: CartStatus): void {
    this.status = cartStatus.status;
    this.setCartId(cartStatus.id);
    this.total.next(cartStatus.total);
    this.pendingAmount.next(cartStatus.pending_amount);
    this.setLines(cartStatus.lines);
    this.requiresAccount.next(cartStatus.requiresAccount);
    this.items.next(cartStatus.items.filter((i) => ![ProductType.TicketService].includes(i.type)));
    this.setIntegrationReservations(cartStatus.integration_reservations);
    this.discounts.next(cartStatus.discounts ?? []);
    this.updateTicketsDiscount(
      cartStatus.lines,
      cartStatus.discounts ?? [],
      new Set(cartStatus.items.map((item) => item.productId))
    );
  }

  /**
   * Marks the order the payment step is holding as no longer matching the cart, so the next
   * `createPendingOrder` posts a new one instead of handing back the one it already has.
   *
   * Every mutation of what is being bought has to go through here. The buyer can walk back out of
   * the payment step, change the basket and return, and the order created on the first visit knows
   * nothing about the change — it would charge the old amount for the old lines. Whatever was
   * applied *to that order* (gift cards, bioscoop cards, discount codes, the chosen method) goes
   * with it: the new order will not carry any of it.
   */
  invalidatePendingOrder(): void {
    if (!this._pendingOrder.value) {
      return;
    }
    this._pendingOrderDirty = true;
    this.discounts.next([]);
    this.giftCards.next([]);
    this.boscoopcards.next([]);
    this._paymentMethod.next(null);
  }

  /** True while {@link invalidatePendingOrder} has outdated the held order and no new one exists yet. */
  get pendingOrderStale(): boolean {
    return this._pendingOrderDirty;
  }

  private cancelAndClearPendingOrder(): Observable<void> {
    this.invalidatePendingOrder();
    return of(undefined);
  }

  /**
   * Sync ticket seat assignments from cart lines.
   * When auto-seat-selection is enabled, the backend assigns seats on each
   * ticket add/remove and may re-optimize all seats. This reads the current
   * seat_id from cart lines and updates the Ticket objects in-place so the
   * seating plan can display them as pre-selected.
   */

  private syncTicketSeatsFromLines(): void {
    const currentLines = this.lines.value;
    if (!currentLines) return;

    this.tickets.value.forEach((ticket) => {
      if (!ticket.reservationId) return;
      const line = currentLines.find((l) => l.id === ticket.reservationId);

      if (line?.seat_id) {
        if (ticket.seat?.id !== line.seat_id) {
          ticket.assignSeat(this.seatsState.getSeatData(line.seat_id));
        }
      } else if (ticket.seat) {
        ticket.unassignSeat();
      }
    });
  }

  createHold(): Observable<string> {
    return this.cartsApiService.holdCart(this.cartId.value).pipe(map((cart) => cart.reservation_code));
  }

  clearCartState(): void {
    this.cartId.next(null);
    this.setTickets([]);
    this.items.next([]);
    this.lines.next([]);
    this.requiresAccount.next(null);
    this.deliveryMethod.next(DeliveryType.Unknown);
    this.total.next(0);
    this.pendingAmount.next(0);
    this.reserving.next(false);
    // stopTimer, not just `time.next(null)`: the running countdown has to be unsubscribed too, or it
    // keeps ticking on a cart that no longer exists and eventually flags a timeout. The ticket flow
    // never noticed because it leaves the checkout wrapper behind on `/order/:orderId`, while the
    // subscription flow's confirmation is a step inside the wrapper and would get the expired dialog
    // on top of a paid order.
    this.stopTimer();
    this.timedOut.next(false);
    this._status.next(null);
    this._paymentMethod.next(null);
    this._integrationReservations.next(null);
    this.discounts.next([]);
    this.giftCards.next([]);
    // Balances are keyed by code and a code belongs to a customer, not to a cart — but the next cart
    // may well be a different customer's, so they go with everything else.
    this.giftCardBalances.clear();
    this.boscoopcards.next([]);
    this._pendingOrder.next(null);
    this._pendingOrderDirty = false;
    this._cartOrders.next([]);
    // The watermark is per cart: left standing, the next cart's first answer reads as stale and the
    // shop opens on an empty basket.
    this.lastAppliedServerTime = 0;
  }

  setCart(cart: CartDto, products: Product[]): void {
    this.cartId.next(cart.id);
    // A restore, not an incremental paint, so it always applies and only ever raises the watermark.
    // Gating it out would skip `addTickets` — the one place cart lines become Ticket objects — and
    // leave the buyer on a restored cart id with an empty basket.
    this.acceptCartView(cart.server_time);
    this.addTickets(cart.summary.lines, products);
    this.reserving.next(false);
    this.setDeliveryMethod();
    this.status = cart.status;
    this.giftCards.next(cart.summary.gift_cards);
    this.discounts.next(cart.summary.discounts ?? []);
    this.setRowsFromSummary(cart.summary);
    this.setLines(cart.summary.lines);
    this.setIntegrationReservations(cart.summary.integration_reservations);
    this.total.next(cart.summary.total_amount);
    this.pendingAmount.next(cart.summary.pending_amount);
    this._cartOrders.next(cart.summary.orders ?? []);
  }

  private addTickets(lines: CartSummaryLineDto[], products: Product[]) {
    const tickets = [];
    lines.forEach((line) => {
      const product = products.find((p) => p.id === line.product_id);
      if (product === undefined || product.type !== ProductType.TicketService) return;
      if (line.seat_id) {
        const seat = this.seatsState.getSeatData(line.seat_id);
        tickets.push(new Ticket(seat, product, line.id));
      } else {
        tickets.push(new Ticket(null, product, line.id));
      }
    });
    this.setTickets([...this.tickets.value, ...tickets]);
  }

  /**
   * Republishes both halves of what the cart preview draws from one cart summary: the item rows and
   * the discount every row carries, tickets included. Always both — the backend moves an auto-applied
   * discount between rows on any cart change, so refreshing one without the other leaves the preview
   * showing a discount the cart no longer has (or none where it now has one).
   */
  private setRowsFromSummary(summary: CartDto['summary']): void {
    const discounts = summary.discounts ?? [];
    const rowProductIds = new Set(summary.products.map((product) => product.product_id));

    this.items.next(
      summary.products
        .map((product) => this.mapToCartItem(product, summary.lines, discounts, rowProductIds))
        .filter((i) => ![ProductType.TicketService].includes(i.type))
    );
    this.updateTicketsDiscount(summary.lines, discounts, rowProductIds);
  }

  private mapToCartItem(
    product: CartSummaryProductDto,
    lines: CartSummaryLineDto[],
    discounts: CartDiscountCode[],
    rowProductIds: ReadonlySet<string>
  ): CartItem {
    const productLines = lines.filter((line) => line.product_id === product.product_id);
    const { discountName, discountedAmount } = resolveRowDiscount(
      productLines.map((line) => line.id),
      lines,
      discounts,
      rowProductIds
    );

    return {
      productId: product.product_id,
      lines: productLines,
      parentCartLineId: lines.find((line) => line.parent_cart_line_id)?.parent_cart_line_id || null,
      title: product.title,
      amount: product?.amount,
      quantity: product?.quantity,
      total: product?.total,
      type: product?.type,
      discountName,
      discountedAmount,
    };
  }

  private updateTicketsDiscount(
    lines: CartSummaryLineDto[],
    discounts: CartDiscountCode[],
    rowProductIds: ReadonlySet<string>
  ): void {
    const tickets = this.tickets.value;
    tickets.forEach((ticket) => {
      const { discountName, discountedAmount } = resolveRowDiscount(
        [ticket.reservationId],
        lines,
        discounts,
        rowProductIds
      );
      ticket.setDiscount(discountName, discountedAmount);
    });
    this.tickets.next([...tickets]);
  }
}
